import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import LogisticsCompany from "@/enum/LogisticsCompany";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import { useAddressDeliverableStore } from "@/scenes/OrderManagement/store/AddressDeliverableStore";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { AddressDeliverable } from "@/services/api/addressDeliverableCheck";
import { getList } from "@/services/api/setting/waybillTemplate";
import { LoadingOutlined } from "@ant-design/icons";
import { useRequest } from "ahooks";
import { Button, Radio, Spin, Tooltip } from "antd";
import { observer } from "mobx-react-lite";
import React, { useEffect, useMemo, useState } from "react";
import "./index.less";

interface AddressDeliverableCheckPaneProps {}

function getUniqueLogisticsCompany(waybillTemplates: WaybillTemplateDTO[]) {
    if (!waybillTemplates || !waybillTemplates.length) return [];

    const logisticsCompanys = new Set<LogisticsCompany>();

    for (let i = 0; i < waybillTemplates.length; i++) {
        const template = waybillTemplates[i];

        const logisticsCompany = template.logisticsCompany;

        logisticsCompanys.add(logisticsCompany);
    }

    return [...logisticsCompanys];
}

const deliverableCheckSupportLogisticsCompanies = [
    LogisticsCompany.SF,
    LogisticsCompany.EMS,
    LogisticsCompany.ZJS,
    LogisticsCompany.POSTB,
    LogisticsCompany.DBKD,
    LogisticsCompany.FEDEX,
    LogisticsCompany.RRS,
    LogisticsCompany.SNWL,
    LogisticsCompany.DN,
    LogisticsCompany.KDLL,
    LogisticsCompany.KUAYUE,
    LogisticsCompany.RFD,
    LogisticsCompany.YCKY,
    LogisticsCompany.JIUYESCM,
    LogisticsCompany.JT,
    LogisticsCompany.YTO,
    LogisticsCompany.ZTO,
    LogisticsCompany.UC,
    LogisticsCompany.GTO,
    LogisticsCompany.QFKD,
    LogisticsCompany.STO,
    LogisticsCompany.YUNDA,
    LogisticsCompany.ANE56,
    LogisticsCompany.XFWL,
    LogisticsCompany.BSYP,
    LogisticsCompany.SZKKE,
    LogisticsCompany.YUNDA_TC,
    LogisticsCompany.PADTF,
    LogisticsCompany.FENGWANG,
    LogisticsCompany.HTKY,
];

const AddressDeliverableCheckPane: React.FC<AddressDeliverableCheckPaneProps> = observer((props) => {
    const { loading, data: waybillTemplates } = useRequest(getList);
    const orderManagementStore = useOrderManagementStore();

    const orders = orderManagementStore.table.orders;

    const [active, updateActive] = useState<LogisticsCompany>(undefined);

    const logisticsCompanies = useMemo(
        () =>
            getUniqueLogisticsCompany(waybillTemplates).filter((it) =>
                deliverableCheckSupportLogisticsCompanies.includes(it),
            ),
        [waybillTemplates],
    );

    const {
        loading: loading2,
        getAddressDeliverable,
        clearAddressDeliverableCheckResults,
        orderDeliverableMap,
    } = useAddressDeliverableStore();

    useEffect(() => {
        clearAddressDeliverableCheckResults();

        if (active && orders.length) {
            getAddressDeliverable(orders, active);
        }
    }, [orders?.map(({ id }) => id).join("|")]);

    function handleRadioChange(e) {
        const logisticsCompany = e.target.value;

        updateActive(logisticsCompany);

        clearAddressDeliverableCheckResults();

        getAddressDeliverable(orders, logisticsCompany);
    }

    const deliverableOrders = orders?.filter(
        ({ id }) => orderDeliverableMap.get(id) === AddressDeliverable.DELIVERABLE,
    );

    function handleSelectAll() {
        orders?.forEach((order) => {
            if (
                !orderManagementStore.table.orderLockStore.lockOrderIdMapping.get(order.id) &&
                orderDeliverableMap.get(order.id) === AddressDeliverable.DELIVERABLE
            ) {
                orderManagementStore.table.selectOrder(order.id);
            }
        });
    }

    return (
        <Spin spinning={loading}>
            <div
                style={{ border: "1px #dddddd solid", padding: "6px 10px", verticalAlign: "middle", fontSize: "14px" }}
                className="address-deliverable-check-pane flex"
            >
                <div className="mr5" style={{ flex: "0 0 auto" }}>
                    派送范围检测:
                </div>

                <Radio.Group onChange={handleRadioChange} value={active}>
                    {logisticsCompanies.map((logisticsCompany) => {
                        const checked = logisticsCompany === active;

                        return (
                            <Radio className="mr5 fs12" key={logisticsCompany} value={logisticsCompany}>
                                {LogisticsCompanyNameMap[logisticsCompany]}

                                {loading2 && checked ? (
                                    <Spin
                                        spinning={loading2}
                                        indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />}
                                    />
                                ) : null}

                                {!loading2 && checked && deliverableOrders?.length ? (
                                    <Tooltip title="选中所有可派送的订单">
                                        <Button type="link" className="ml3 select-all-btn" onClick={handleSelectAll}>
                                            全选({deliverableOrders.length})
                                        </Button>
                                    </Tooltip>
                                ) : null}
                            </Radio>
                        );
                    })}
                </Radio.Group>
            </div>
        </Spin>
    );
});

interface _AddressDeliverableCheckPaneProps {}

const _AddressDeliverableCheckPane: React.FC<_AddressDeliverableCheckPaneProps> = function (props) {
    const orderManagementStore = useOrderManagementStore();

    if (!orderManagementStore.table.showAddressReachableCheckPane) {
        return null;
    }

    return <AddressDeliverableCheckPane />;
};

export default observer(_AddressDeliverableCheckPane);
